import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { ArrowRight, Camera, Sparkles, ChevronLeft, ChevronRight, Ticket, MapPin, HeartHandshake, Maximize2, X } from "lucide-react";
import { useSEO } from "../lib/useSEO";
import { PageDecor } from "../components/decor/PageDecor";
import { thumb, fallbackToFull } from "../lib/imageThumb";
import { reservationFor } from "../lib/spectacleReservations";
import { useChantiers } from "../lib/useChantiers";
import { trierRepresentations, jourLisible, heureLisible } from "../lib/representations";

// Chaque spectacle = 1 entrée. Ajoute simplement une nouvelle édition en haut du tableau.
// `date_iso` alimente la page « Dates importantes » et le balisage schema.org.
export const SPECTACLES = [
  // Les spectacles de juin 2027 (école, 30.10.2026) : titres, salles et affiches donnés par
  // l'école ; `billets` mène à la billetterie quand elle est ouverte (version de test d'abord).
  // `representations` : une ligne par représentation sur la page (mêmes dates que
  // backend/console/billetterie.py, REPRESENTATIONS).
  {
    id: "danse-2027",
    title: "Héros & Vilains",
    subtitle: "Spectacle de fin d'année 2027 · Enfants et ados",
    date_label: "12 & 13 juin 2027",
    date_iso: "2027-06-12",
    date_fin_iso: "2027-06-13",
    location: "Grande salle de Thierrens, Rue du Collège, 1410 Thierrens",
    representations: [
      { date: "2027-06-12", heure: "18:00" },
      { date: "2027-06-13", heure: "11:00" },
      { date: "2027-06-13", heure: "15:00" },
    ],
    portes: "Ouverture des portes 30 minutes avant · placement libre",
    upcoming: true,
    description:
      "Les enfants et les ados de 1000 ARTS montent sur scène : hip-hop, classique, contemporain, girly, breakdance et les groupes de compétition. Des mois de travail, de progrès et de complicité réunis dans un spectacle vivant, coloré et porté par l'énergie du groupe.",
    cover: "/img/spectacles/heros-et-vilains.webp",
    billets: "heros-et-vilains",
    affiche: true,
  },
  {
    id: "comedie-musicale-2027",
    title: "Le Monde de l'Autre",
    subtitle: "Spectacle de fin d'année 2027 · Comédie musicale, compagnie et adultes",
    date_label: "19 & 20 juin 2027",
    date_iso: "2027-06-19",
    date_fin_iso: "2027-06-20",
    location: "Grande salle de Goumoens-la-Ville, Route de Penthéréaz 4, 1376 Goumoens-la-Ville",
    representations: [
      { date: "2027-06-19", heure: "18:00", note: "ouverte si la demande est forte" },
      { date: "2027-06-20", heure: "11:00" },
      { date: "2027-06-20", heure: "15:00" },
    ],
    portes: "Ouverture des portes 30 minutes avant · placement libre",
    upcoming: true,
    // Le synopsis, pour donner envie (école, 31.10.2026).
    synopsis: "Et si, pour échapper à une réalité trop difficile, il suffisait de réécrire l’histoire ? Deux sœurs basculent dans un monde né de l’imagination, où tout devait être paix et amour… jusqu’au jour où « l’Autre » apparaît.",
    description:
      "Chant, jeu et danse réunis : les élèves de comédie musicale, la compagnie et les adultes de danse présentent le spectacle préparé toute l'année. Décors, costumes et numéros enchaînés — une vraie création de scène.",
    cover: "/img/spectacles/le-monde-de-l-autre.webp",
    billets: "le-monde-de-l-autre",
    affiche: true,
  },
  {
    id: "noel-2026",
    title: "Spectacle de Noël 2026",
    subtitle: "Comédie Musicale · Soirée solidaire",
    date_label: "07.12.2026",
    date_iso: "2026-12-07",
    reservation_url: "/inscription/spectacle?spectacle=noel-2026",
    location: "Maison Villageoise, Cugy VD",
    upcoming: true,
    description:
      "À l'approche des fêtes, les élèves de Comédie Musicale de 1000 ARTS montent sur scène pour célébrer la magie de Noël. Chant, danse et théâtre s'entremêlent dans un décor scintillant préparé avec soin — un moment suspendu, tendre et lumineux, à partager en famille. Les bénéfices de la soirée sont intégralement reversés à une association, pour prolonger l'esprit de Noël bien au-delà de la scène.",
    cover: "/img/ced5ea8e9f.webp",
  },
  {
    id: "gala-2026",
    title: "Gala 2026",
    subtitle: "Le grand spectacle annuel 1000 ARTS",
    date_label: "6 & 7 juin 2026",
    date_iso: "2026-06-06",
    date_fin_iso: "2026-06-07",
    location: "Salle du Battoir, Goumoens-la-Ville",
    description:
      "Une année entière de travail, plus de 100 élèves sur scène, 15 disciplines mises en lumière. Une soirée magique pour célébrer la passion, le progrès et la joie de partager l'art de la scène.",
    cover: "/img/3fc8b58290.webp",
    carousel: [
      "/img/3fc8b58290.webp",
      "/img/gala-2026-7.webp",
      "/img/gala-2026-3.webp",
      "/img/gala-2026-4.webp",
      "/img/gala-2026-6.webp",
      "/img/gala-2026-5.webp",
    ],
    photographer: "Suman Photography",
  },
  {
    id: "halloween-2025",
    title: "Halloween 2025",
    subtitle: "Le spectacle créé pendant le stage d'octobre",
    date_label: "Octobre 2025",
    location: "Salles 1000 ARTS, Cugy VD",
    description:
      "Un rendez-vous magique et frissonnant : nos élèves ont créé leur propre spectacle pendant le stage d'octobre. Costumes, chorégraphies, décors et scénographie — tout est le fruit d'une semaine intensive d'imagination et de travail collectif. Une belle façon de fêter Halloween tout en développant créativité, présence scénique et esprit d'équipe.",
    covers: [
      "/img/aafc9e8b2d.webp",
      "/img/3c1e83c8f7.webp",
    ],
    cover: "/img/aafc9e8b2d.webp",
  },
];

/**
 * Ordre d'affichage : le spectacle le PLUS PROCHE d'abord (c'est celui pour lequel on
 * réserve), puis les suivants, puis les éditions passées de la plus récente à la plus
 * ancienne. Aucun classement à maintenir à la main : il suffit d'ajouter une édition.
 */
export function ordreAffichage(liste, now = new Date()) {
  const aujourdhui = now.toISOString().slice(0, 10);
  const cle = (s) => s.date_fin_iso || s.date_iso || "";
  const futurs = liste.filter((s) => cle(s) >= aujourdhui).sort((a, b) => cle(a).localeCompare(cle(b)));
  const passes = liste.filter((s) => cle(s) && cle(s) < aujourdhui).sort((a, b) => cle(b).localeCompare(cle(a)));
  const sansDate = liste.filter((s) => !cle(s));
  return [...futurs, ...passes, ...sansDate];
}

export default function Spectacles() {
  useSEO({
    title: "Spectacles — Gala annuel 1000 ARTS · Cugy VD",
    description:
      "Découvre les spectacles de 1000 ARTS : Gala annuel Mil'Air, chorégraphies originales, comédie musicale, hip-hop et arts de la scène. Photos du Gala 2026 par Suman Photography.",
    canonical: "/spectacles",
  });
  const chantiers = useChantiers();
  // Les représentations ouvertes à la vente (billetterie, test d'abord) : la note « ouverte si
  // la demande est forte » du samedi 19 disparaît quand l'école l'ouvre (01.11.2026).
  const [ouvertes, setOuvertes] = useState(null);
  useEffect(() => {
    if (!chantiers.spectacles_juin) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/billetterie`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setOuvertes(Object.fromEntries((d.representations || []).map((x) => [`${x.date} ${x.heure}`, x.ouverte]))))
      .catch(() => {});
  }, [chantiers.spectacles_juin]);

  return (
    <div data-testid="page-spectacles" className="pt-32 pb-24 relative overflow-hidden mesh-bg">
      <PageDecor />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
      <div className="eyebrow mb-3 flex items-center gap-2">
        <Sparkles size={14} /> Sur scène
      </div>
      <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-tight max-w-4xl">
        Nos <span style={{ color: "var(--primary)" }}>spectacles</span>
      </h1>
      <p className="mt-6 text-lg md:text-xl text-[var(--muted)] max-w-3xl leading-relaxed">
        Tout au long de l'année, 1000 ARTS fait vivre la scène à ses élèves : grand Gala annuel, spectacle solidaire de Noël, soirée Halloween, spectacles de fin de stages saisonniers… autant d'occasions de partager le fruit d'un travail collectif. Retrouve ici les événements à venir et les galeries des éditions passées.
      </p>
      <p className="mt-4 text-base">
        <Link to="/dates-importantes" data-testid="spectacles-lien-dates" className="underline" style={{ color: "var(--primary)" }}>
          Voir toutes les dates importantes de la saison
        </Link>
      </p>

      <section className="mt-14 space-y-16">
        {ordreAffichage(SPECTACLES).map((s) => (
          <article
            key={s.id}
            data-testid={`spectacle-${s.id}`}
            className={`rounded-3xl overflow-hidden grid gap-0 shadow-xl bg-white ${s.affiche ? "md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]" : "md:grid-cols-2"}`}
            style={{ border: "1px solid var(--border-soft)" }}
          >
            {/* Sur ordinateur, l'image prend toute la hauteur de l'encart (plus de blanc sous
                l'affiche quand le texte est plus long — Jen, 31.10.2026) ; sur téléphone, 4/3.
                La hauteur minimale ne vaut que sur ordinateur : sur téléphone, avec le 4/3, elle
                élargissait l'image au-delà de l'écran (01.11.2026).
                Une affiche remplit tout le panneau (Jen, 01.11.2026 : « l'affiche plein écran ») :
                au format de l'affiche (2/3) sur téléphone ; sur ordinateur, au moins aussi haute
                que l'affiche le demande (sans dépasser l'écran), entière, jamais recadrée. */}
            <div className={`relative overflow-hidden ${s.affiche ? "aspect-[2/3]" : "aspect-[4/3]"} md:aspect-auto md:h-full md:min-h-[340px]`}>
              {s.affiche && <div aria-hidden="true" className="hidden md:block" style={{ paddingTop: "min(150%, 88vh)" }} />}
              <SpectacleCover spectacle={s} />
              {/* Sur une affiche, ses dates sont déjà écrites en grand : pas d'étiquette par-dessus. */}
              {!s.affiche && (
                <div
                  className="absolute top-4 left-4 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-widest shadow-sm z-10"
                  style={{ background: "rgba(255,255,255,0.95)", color: "var(--primary)" }}
                >
                  {s.date_label}
                </div>
              )}
              {s.upcoming && (
                <div
                  className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest shadow-md z-10"
                  style={{ background: "var(--primary)", color: "#fff" }}
                  data-testid={`spectacle-${s.id}-upcoming-badge`}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                  À venir
                </div>
              )}
            </div>
            <div className="p-8 md:p-10 flex flex-col justify-center">
              <div className="eyebrow" style={{ color: "var(--primary)" }}>{s.subtitle}</div>
              <h2 className="font-display text-3xl md:text-4xl mt-2 mb-4 tracking-tight">{s.title}</h2>
              {s.representations ? (
                <>
                  <Representations spectacle={s} ouvertes={ouvertes} />
                  <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.location)}`} target="_blank" rel="noopener noreferrer"
                    className="flex items-start gap-2 text-sm text-[var(--muted)] mb-5 hover:text-[var(--text)]" data-testid={`spectacle-${s.id}-lieu`}>
                    <MapPin size={15} className="mt-0.5 shrink-0" style={{ color: "var(--primary)" }} />
                    <span>{s.location} · <span className="underline">itinéraire</span></span>
                  </a>
                </>
              ) : (
                <div className="text-sm text-[var(--muted)] mb-4">{s.location}</div>
              )}
              {/* Une seule voix forte, le titre (Jen, 31.10.2026 : « texte trop gros ») : le
                  synopsis en chapeau, lisible et posé ; la description, plus discrète. */}
              {s.synopsis && (
                <p className="text-[17px] md:text-[18px] leading-relaxed font-medium text-[var(--text)] mb-3 max-w-[38rem]"
                  data-testid={`spectacle-${s.id}-synopsis`}>{s.synopsis}</p>
              )}
              <p className="text-[15px] leading-relaxed text-[var(--muted)] mb-6 max-w-[38rem]">{s.description}</p>
              {reservationFor(s.id) && (
                <div data-testid={`spectacle-${s.id}-reservation`}>
                  <div className="text-sm mb-4">
                    <strong>{reservationFor(s.id).time_label}</strong> · {reservationFor(s.id).doors_label} ·{" "}
                    {reservationFor(s.id).restauration} · entrée libre, don libre sur place
                  </div>
                  <Link
                    to={`/inscription/spectacle?spectacle=${s.id}`}
                    data-testid={`spectacle-${s.id}-reserver`}
                    className="btn-primary self-start"
                  >
                    <Ticket size={16} /> Réserver une place
                  </Link>
                  <div className="text-xs text-[var(--muted)] mt-3">
                    {reservationFor(s.id).capacity} places · bénéfices reversés à {reservationFor(s.id).association.name}
                  </div>
                </div>
              )}
              {s.billets && chantiers.spectacles_juin && (
                <div className="flex flex-wrap gap-3">
                  <Link to={`/spectacles/billets?spectacle=${s.billets}`} data-testid={`spectacle-${s.id}-billets`} className="btn-primary">
                    <Ticket size={16} /> Acheter des billets
                  </Link>
                  <Link to={`/spectacles/billets?spectacle=${s.billets}#benevoles`} data-testid={`spectacle-${s.id}-benevoles`} className="btn-ghost">
                    <HeartHandshake size={16} /> Devenir bénévole
                  </Link>
                </div>
              )}
              {s.billets && !chantiers.spectacles_juin && (
                <div className="text-sm text-[var(--muted)] flex items-center gap-2" data-testid={`spectacle-${s.id}-billets-bientot`}>
                  <Ticket size={15} /> Billets en vente bientôt sur ce site.
                </div>
              )}
              {s.photographer && (
                <div className="text-xs text-[var(--muted)] italic mt-4">
                  Photos © {s.photographer}
                </div>
              )}
            </div>
          </article>
        ))}
      </section>

      <section className="mt-20">
        <div
          className="rounded-3xl p-10 md:p-14 text-center"
          style={{ background: "linear-gradient(135deg, var(--primary) 0%, #00A8B0 100%)", color: "#fff" }}
          data-testid="spectacle-next-cta"
        >
          <div className="eyebrow mb-3" style={{ color: "rgba(255,255,255,0.85)" }}>Prochaine édition</div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight max-w-3xl mx-auto">
            Les spectacles de juin 2027 se préparent déjà.
          </h2>
          <p className="mt-6 text-lg text-white/90 max-w-2xl mx-auto">
            Rejoins un cours pour être sur scène l'année prochaine — la participation au spectacle est incluse dans l'inscription semestrielle.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link
              to="/inscription"
              data-testid="spectacle-cta-inscription"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold shadow-md hover:shadow-xl hover:scale-[1.02] transition-all"
              style={{ background: "#fff", color: "var(--primary)" }}
            >
              S'inscrire <ArrowRight size={16} />
            </Link>
            <Link
              to="/cours"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full border-2 border-white text-white font-semibold hover:bg-white hover:text-[var(--primary)] transition-colors"
            >
              Explorer les cours
            </Link>
          </div>
        </div>
      </section>
      </div>
    </div>
  );
}

const majuscule = (t) => t.charAt(0).toUpperCase() + t.slice(1);

/* L'affiche en grand, par-dessus toute la page : un clic, la croix ou Échap la ferme. */
function AfficheEnGrand({ src, titre, onFermer }) {
  useEffect(() => {
    const touche = (e) => { if (e.key === "Escape") onFermer(); };
    const avant = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", touche);
    return () => { document.removeEventListener("keydown", touche); document.body.style.overflow = avant; };
  }, [onFermer]);
  return createPortal(
    <div role="dialog" aria-modal="true" aria-label={`Affiche · ${titre}`} onClick={onFermer} data-testid="affiche-en-grand"
      className="fixed inset-0 flex items-center justify-center p-3 md:p-8 cursor-zoom-out"
      style={{ background: "rgba(10,15,25,0.9)", zIndex: 2147483600 }}>
      <img src={src} alt={`Affiche · ${titre}`} className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" />
      <button type="button" aria-label="Fermer" onClick={onFermer} data-testid="affiche-fermer"
        className="absolute top-3 right-3 md:top-5 md:right-5 w-11 h-11 rounded-full grid place-items-center shadow-lg"
        style={{ background: "rgba(255,255,255,0.92)", color: "#0F1B2D" }}>
        <X size={20} />
      </button>
    </div>,
    document.body,
  );
}

/* Les représentations : une par ligne, dans l'ordre chronologique (Jen, 30.10.2026). */
function Representations({ spectacle, ouvertes }) {
  const reps = trierRepresentations(spectacle.representations);
  return (
    <div className="mb-4" data-testid={`spectacle-${spectacle.id}-horaires`}>
      <div className="text-[11px] font-semibold uppercase tracking-widest text-[var(--muted)] mb-2">
        {reps.length} représentation{reps.length > 1 ? "s" : ""}
      </div>
      <ul className="space-y-1.5">
        {reps.map((r) => (
          <li key={`${r.date}-${r.heure}`} className="flex items-baseline gap-2.5 text-[15px]">
            <span className="w-1.5 h-1.5 rounded-full shrink-0 -translate-y-0.5" style={{ background: "var(--primary)" }} aria-hidden="true" />
            <span>
              <span className="font-semibold">{majuscule(jourLisible(r.date))}</span> · {heureLisible(r.heure)}
              {r.note && !ouvertes?.[`${r.date} ${r.heure}`] && <span className="text-sm text-[var(--muted)]"> — {r.note}</span>}
            </span>
          </li>
        ))}
      </ul>
      {spectacle.portes && <div className="text-xs text-[var(--muted)] mt-2">{spectacle.portes}</div>}
    </div>
  );
}

/**
 * Cover d'un spectacle — img simple si `cover` seul, mini-carrousel auto+manuel si `covers[]`.
 */
function SpectacleCover({ spectacle }) {
  const imgs = spectacle.carousel && spectacle.carousel.length > 0
    ? spectacle.carousel
    : (spectacle.covers && spectacle.covers.length > 0 ? spectacle.covers : [spectacle.cover]);
  const [idx, setIdx] = useState(0);
  const [grand, setGrand] = useState(false);
  const fermer = useCallback(() => setGrand(false), []);
  useEffect(() => {
    if (imgs.length < 2) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % imgs.length), 4500);
    return () => clearInterval(t);
  }, [imgs.length]);

  // Une affiche (portrait) se montre en entier ; un clic l'ouvre en grand (Jen, 30.10.2026).
  // Elle remplit tout le panneau, sans recadrage (Jen, 01.11.2026 : « l'affiche plein
  // écran ») ; le fond flou de la même image ne se voit que si le panneau est plus large
  // que l'affiche. « Agrandir » se pose en haut à gauche, sur le rideau : en bas, il cachait
  // l'adresse du site écrite sur l'affiche.
  if (spectacle.affiche) {
    return (
      <>
        <img src={imgs[0]} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover blur-2xl scale-110 opacity-60" loading="lazy" />
        <button type="button" onClick={() => setGrand(true)} className="absolute inset-0 w-full h-full cursor-zoom-in"
          aria-label={`Voir l'affiche de ${spectacle.title} en grand`} data-testid={`spectacle-${spectacle.id}-affiche-ouvrir`}>
          <img src={imgs[0]} alt={`Affiche · ${spectacle.title}`} className="absolute inset-0 w-full h-full object-contain" loading="lazy" data-testid={`spectacle-${spectacle.id}-affiche`} />
          <span className="absolute top-3 left-3 md:top-4 md:left-4 z-10 inline-flex items-center gap-1 md:gap-1.5 rounded-full px-2.5 py-1 md:px-3 md:py-1.5 text-[11px] md:text-xs font-semibold text-white shadow-md"
            style={{ background: "rgba(15,27,45,0.7)", backdropFilter: "blur(4px)" }}>
            <Maximize2 size={12} /> Agrandir
          </span>
        </button>
        {grand && <AfficheEnGrand src={imgs[0]} titre={spectacle.title} onFermer={fermer} />}
      </>
    );
  }
  if (imgs.length === 1) {
    return <img src={thumb(imgs[0])} onError={fallbackToFull(imgs[0])} alt={spectacle.title} className="w-full h-full object-cover" loading="lazy" />;
  }
  return (
    <div data-testid={`spectacle-${spectacle.id}-carousel`} className="contents">
      {imgs.map((src, i) => (
        <img
          key={i}
          src={thumb(src)} onError={fallbackToFull(src)}
          alt={`${spectacle.title} — photo ${i + 1}`}
          data-testid={`spectacle-${spectacle.id}-img-${i}`}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
          style={{ opacity: idx === i ? 1 : 0 }}
          loading={i === 0 ? "eager" : "lazy"}
          decoding="async"
        />
      ))}
      <div
        className="absolute top-3 right-3 z-10 px-2.5 py-1 rounded-full text-[11px] font-semibold"
        data-testid={`spectacle-${spectacle.id}-counter`}
        style={{ background: "rgba(15,27,45,0.6)", color: "#fff", backdropFilter: "blur(4px)" }}
      >
        {idx + 1} / {imgs.length}
      </div>
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2 z-10">
        {imgs.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setIdx(i)}
            data-testid={`spectacle-${spectacle.id}-dot-${i}`}
            aria-label={`Photo ${i + 1}`}
            className="w-2.5 h-2.5 rounded-full transition-all"
            style={{ background: idx === i ? "#fff" : "rgba(255,255,255,0.5)" }}
          />
        ))}
      </div>
      <button
        type="button"
        onClick={() => setIdx((i) => (i - 1 + imgs.length) % imgs.length)}
        aria-label="Photo précédente"
        data-testid={`spectacle-${spectacle.id}-prev`}
        className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center shadow-md z-10 hover:scale-110 transition-transform"
        style={{ background: "rgba(255,255,255,0.85)" }}
      >
        <ChevronLeft size={16} />
      </button>
      <button
        type="button"
        onClick={() => setIdx((i) => (i + 1) % imgs.length)}
        aria-label="Photo suivante"
        data-testid={`spectacle-${spectacle.id}-next`}
        className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center shadow-md z-10 hover:scale-110 transition-transform"
        style={{ background: "rgba(255,255,255,0.85)" }}
      >
        <ChevronRight size={16} />
      </button>
    </div>
  );
}
